import React from 'react';
import { connect } from 'react-redux';
import ReactSwipe from 'react-swipe';

import { AppContext } from '@prisjakt/ui-app-context';
import { DIRECTIONS } from '@prisjakt/ui-hideable-box';

import { logoutRequest } from '../../../account/actions';
import { AppState } from '../../../../reducers';
import { setMobileSidebarView } from './actions';

import Menu from '../../../navigation/components/mobile-menu/menu';
import Categories from '../../../navigation/components/mobile-menu/components/categories';
import MobileMenuButton from '../../../navigation/components/mobile-menu-button';
import UserMenu from '../user-menu';
import List from '../../views/list';
import Lists from '../../views/lists';
import Alerts from '../../views/alerts';
import UserView from '../../views/user';
import SidebarHeader from './sidebar-header-mobile';

import * as S from './styles';

import { VIEWS } from './constants';
import { Category, View, CategoryMenuState } from '../../types';
import { User } from '../../../../types';
import { Draggable } from '../sharedTypes';

type OwnProps = {
  onClose: () => void;
  menuHistory: Category[];
  onSelectHistory: (id?: string) => void;
  categories: Category[];
  isTraversingUpwards: boolean;
  onMenuChange: (state: CategoryMenuState) => void;
  open: boolean;
};

type StateProps = {
  selectedList: number;
  isAuthenticated?: boolean;
  me: User;
  currentView: View;
};

type DispatchProps = {
  onLogout: (message: string) => void;
  setMobileSidebarView: (view: View) => void;
  openListsMobile: () => void;
  openAlertsMobile: () => void;
};

type State = {
  currentView: View;
  previousCategory: Category | null;
  draggable: Draggable;
  viewportHeight: string;
};

type Props = OwnProps & StateProps & DispatchProps;

const reactSwipeStyle = {
  child: {
    width: '100vw',
  },
  container: {},
  wrapper: {},
};

class SidebarMobile extends React.Component<Props, State> {
  state = {
    previousCategory: null,
    draggable: false,
    viewportHeight: '100vh',
  } as State;

  componentDidMount() {
    window.addEventListener('resize', this.updateViewportHeight);
    this.updateViewportHeight();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateViewportHeight);
  }

  updateViewportHeight = () => {
    if (!IS_BROWSER) return;

    // On mobile `vh` does not take browser UI into consideration,
    // causing 100vh to be higher than what is visible.
    // `window.innerHeight` is the actual visible height of the window
    // and will be updated every render.

    this.setState({
      viewportHeight: `${window.innerHeight}px`,
    });
  };

  selectHistory = (categoryId?: string) => {
    const { onSelectHistory, menuHistory } = this.props;

    this.setState({ previousCategory: menuHistory[menuHistory.length - 1] });

    onSelectHistory(categoryId);
  };

  closeMenu = () => {
    this.setState({ previousCategory: null });
    this.props.onClose();
  };

  closeMenuAndResetState = () => {
    this.setState({
      previousCategory: null,
    });

    this.props.setMobileSidebarView(VIEWS.ROOT);
    this.props.onClose();
  };

  changeMenuLevel = (category: Category) => {
    const { onMenuChange, menuHistory } = this.props;

    this.setState({
      previousCategory: menuHistory[menuHistory.length - 1],
    });

    onMenuChange({
      categories: category.children,
      parent: category,
    });
  };

  // Utility functions to sort categories by id. The actual sorting method doesn't matter in this case, as long as it's stable.
  // This is so that React's reconciliation algorithm will re-use any previously rendered elements.
  getCategoryId = (category: Category | null) =>
    (category && category.id) || '';

  compareById = (a: Category | null, b: Category | null) =>
    this.getCategoryId(a).localeCompare(this.getCategoryId(b), 'en');

  renderSidebarCategoryView = (category: Category | null) => {
    if (!category) {
      return null;
    }

    const {
      menuHistory,
      isAuthenticated,
      me,
      isTraversingUpwards,
      currentView,
    } = this.props;
    const { previousCategory } = this.state;
    const parentCategory = menuHistory[menuHistory.length - 2];
    const currentCategory = menuHistory[menuHistory.length - 1];
    const isCurrentCategory = category.id === currentCategory.id;
    const isRootCategory = !category.name;

    return (
      <S.SidebarView
        key={category.id}
        visible={isCurrentCategory && currentView === VIEWS.ROOT}
        initialTransition={!!previousCategory}
        renderHiddenChildren
        slide={isRootCategory ? DIRECTIONS.LEFT : undefined}
      >
        {isRootCategory && (
          <UserMenu
            visible
            onLinkClick={this.closeMenu}
            onOpenView={this.onOpenView}
            me={me}
            isAuthenticated={isAuthenticated}
          />
        )}
        {!isRootCategory && (
          <SidebarHeader
            title={category.name}
            onClickHandler={() => this.selectHistory(parentCategory.id)}
          />
        )}
        {
          <Categories
            category={category}
            changeMenuLevel={this.changeMenuLevel}
            isTraversingUpwards={isTraversingUpwards}
            closeMenu={this.closeMenu}
            menuHistory={menuHistory}
            previousCategory={previousCategory}
            showSidebar
          />
        }
      </S.SidebarView>
    );
  };

  onLogoutClick = () => {
    const { t } = this.context;

    this.closeMenuAndResetState();
    this.props.onLogout(t('account', 'logout.successful'));
  };

  onOpenView = (view: View) => {
    this.setState({
      draggable: false,
    });

    this.props.setMobileSidebarView(view);
  };

  onSwipeTransitionEnd = (index: number) => {
    // Close menu on swipe away
    if (index > 0) {
      this.closeMenuAndResetState();
    }
  };

  swipeOptions = {
    continuous: false,
    transitionEnd: this.onSwipeTransitionEnd,
  };

  toggleDraggable = () => {
    this.setState(prevState => ({
      draggable: !prevState.draggable,
    }));
  };

  render() {
    const {
      selectedList,
      isAuthenticated,
      me,
      categories,
      menuHistory,
      open,
      currentView,
    } = this.props;
    const { previousCategory, viewportHeight } = this.state;
    const { t } = this.context;
    const visible = open && categories && categories.length > 0;
    const currentCategory = menuHistory[menuHistory.length - 1];

    return (
      <>
        <S.Overlay visible={visible} renderHiddenChildren />
        <S.FakeHeader visible={visible} renderHiddenChildren />
        <S.Sidebar visible={visible} data-test="SidebarMobile">
          <ReactSwipe swipeOptions={this.swipeOptions} style={reactSwipeStyle}>
            <S.SwipeTarget height={viewportHeight}>
              <S.MenuButtonArea visible={!visible}>
                <MobileMenuButton />
              </S.MenuButtonArea>
              <S.MenuButtonArea visible={visible}>
                <S.CloseButton aria-label={t('symbols', 'symbol.close')}>
                  <S.Close />
                </S.CloseButton>
              </S.MenuButtonArea>
              <Menu closeMenu={this.closeMenuAndResetState} showSidebar>
                <>
                  {/* Render the previous and the current category in order to be able to transition smoothly between states. */
                  [previousCategory, currentCategory]
                    .sort(this.compareById)
                    .map(this.renderSidebarCategoryView)}
                  {
                    <>
                      <S.SidebarView
                        key="lists"
                        visible={currentView === VIEWS.LISTS}
                        slide={
                          currentView === VIEWS.ROOT
                            ? DIRECTIONS.RIGHT
                            : DIRECTIONS.LEFT
                        }
                        renderHiddenChildren
                      >
                        <Lists
                          onOpenView={this.onOpenView}
                          onLinkClick={this.closeMenu}
                          draggable={this.state.draggable}
                          toggleDraggable={this.toggleDraggable}
                          isMobile
                        />
                      </S.SidebarView>

                      <S.SidebarView
                        key={`list${selectedList}`}
                        visible={currentView === VIEWS.LIST}
                        slide={DIRECTIONS.RIGHT}
                        renderHiddenChildren
                      >
                        <List
                          id={selectedList}
                          onOpenView={this.onOpenView}
                          onLinkClick={this.closeMenu}
                          draggable={this.state.draggable}
                          toggleDraggable={this.toggleDraggable}
                          isMobile
                        />
                      </S.SidebarView>

                      <S.SidebarView
                        key="alerts"
                        visible={currentView === VIEWS.ALERTS}
                        slide={DIRECTIONS.RIGHT}
                        renderHiddenChildren
                      >
                        <Alerts
                          onLinkClick={this.closeMenu}
                          onOpenView={this.onOpenView}
                          isMobile
                        />
                      </S.SidebarView>

                      {isAuthenticated && (
                        <S.SidebarView
                          key="user"
                          visible={currentView === VIEWS.USER}
                          slide={DIRECTIONS.RIGHT}
                        >
                          <SidebarHeader
                            title={me.displayName || ''}
                            onClickHandler={() => this.onOpenView(VIEWS.ROOT)}
                          />
                          <UserView
                            me={me}
                            closeMenu={this.closeMenu}
                            onLogoutClick={this.onLogoutClick}
                          />
                        </S.SidebarView>
                      )}
                    </>
                  }
                </>
              </Menu>
            </S.SwipeTarget>
            <S.SwipeTarget />
          </ReactSwipe>
        </S.Sidebar>
      </>
    );
  }
}

SidebarMobile.contextType = AppContext;

const mapStateToProps = (state: AppState) => ({
  selectedList: state.areas.sidebar.selectedList,
  me: state.areas.account.me,
  isAuthenticated: state.areas.account.isAuthenticated,
  currentView: state.areas.mobileSidebar.currentView,
});

const dispatchProps = {
  onLogout: logoutRequest,
  setMobileSidebarView,
};

export default connect(
  mapStateToProps,
  dispatchProps
)(SidebarMobile);
